import i18next from 'i18next';
import permissions from '@shared/config/permissions';
import { INFO_PANE_PAGES } from 'redux_store';
import {
  ENCOUNTER_CLAIMS_STATUSES_OPTIONS,
  BILLING_CLAIMS_STATUSES_OPTIONS,
  CLAIM_STATUS_SELECTOR_OPTIONS,
  CLAIM_ENTITY_TYPE_SELECTOR_OPTIONS,
} from './statuses';
// TODO(Phase 7): CUSTOM_TAGS_OPTIONS / FILTER_TYPES still live in
// the legacy table infrastructure. They move to shared when the
// table widgets migrate. Until then this entity reaches into
// _legacy/* via the no-restricted-imports exemption that already
// covers entities/** (.eslintrc Group 2).
import { CUSTOM_TAGS_OPTIONS } from '_legacy/components/Shared/CustomTags';
import FILTER_TYPES from '@shared/ui/Table/Filters/constants';

/**
 * Column definition factory for the claims table.
 *
 * Lifted from `_legacy/components/Claims/constants.js` (lines 280-500).
 *
 * Takes `isPatientPage` because the table is reused on the patient
 * dashboard, where the per-patient identity columns (first name, last
 * name, DOB) are redundant and need to be hidden.
 *
 * Schema follows Mantine-React-Table extended with this project's
 * table infrastructure (filter config, cell config, permission
 * gating, `isDefaultCell` toggle).
 */
export const GET_CLAIMS_COLUMNS = isPatientPage => [
  {
    size: 200,
    header: i18next.t('tables:customTags'),
    accessorKey: 'accountCustomTags',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: CUSTOM_TAGS_OPTIONS,
    },
    cell: {
      withBubbles: true,
    },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 220,
    header: i18next.t('tables:account'),
    accessorKey: 'accountBusinessName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 180,
    header: i18next.t('tables:customId'),
    accessorKey: 'accountCustomIdentifier',
    filter: {},
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:mrnNumber'),
    accessorKey: 'patientMrnNumber',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientFirstName'),
    accessorKey: 'patientFirstName',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientLastName'),
    accessorKey: 'patientLastName',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:patientDOB'),
    accessorKey: 'patientDateOfBirth',
    filter: {
      filterType: FILTER_TYPES.DATE,
    },
    hidden: isPatientPage,
    isDefaultCell: true,
  },
  {
    size: 280,
    header: i18next.t('tables:assessmentResource'),
    accessorKey: 'entityName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:orderDate'),
    accessorKey: 'entityOrderTimestamp',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 270,
    header: i18next.t('tables:claimGeneratedTimestamp'),
    accessorKey: 'claimGeneratedTimestamp',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:createdBy'),
    accessorKey: 'createdBy',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:lastModifiedTimestamp'),
    accessorKey: 'lastModifiedTimestamp',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:modifiedBy'),
    accessorKey: 'modifiedBy',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 250,
    header: i18next.t('tables:appointmentDate'),
    accessorKey: 'appointmentTimestamp',
    filter: {
      shouldRange: true,
      hasNoMaxDate: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 220,
    header: i18next.t('tables:entityCompletedTimestamp'),
    accessorKey: 'entityCompletedTimestamp',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 220,
    header: i18next.t('tables:encounterStatus'),
    accessorKey: 'encounterStatus',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: ENCOUNTER_CLAIMS_STATUSES_OPTIONS,
    },
    cell: {
      withBubble: true,
    },
  },
  {
    size: 220,
    header: i18next.t('tables:billingStatus'),
    accessorKey: 'billingStatus',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: BILLING_CLAIMS_STATUSES_OPTIONS,
    },
    cell: {
      withBubble: true,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:claimStatus'),
    accessorKey: 'claimStatus',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: CLAIM_STATUS_SELECTOR_OPTIONS,
    },
    cell: {
      withBubble: true,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:entityType'),
    accessorKey: 'entityType',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: CLAIM_ENTITY_TYPE_SELECTOR_OPTIONS,
    },
  },
  {
    size: 180,
    header: i18next.t('tables:dos'),
    accessorKey: 'dos',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:cptCount'),
    accessorKey: 'cptCount',
    filter: {},
    isDefaultCell: true,
  },
  {
    size: 250,
    header: i18next.t('tables:cptPaid'),
    accessorKey: 'cptPaid',
    filter: {},
    isDefaultCell: true,
  },
];
